import { BuildMode } from "./build-mode";
import { CanvasGestures } from "./canvas-gestures";
import { ControlsPanel } from "./controls-panel";
import { EventLog, type EventLevel } from "./event-log";
import { showFatal, updateHud } from "./hud";
import { ImageControlsPanel } from "./image-controls-panel";
import { InputState } from "./input";
import { Inspector } from "./inspector";
import {
  canvasToWorld,
  getZoom,
  getZoomTarget,
  makeCamera,
  setZoomTarget,
  syncCameraToCanvas,
  updateZoomAnimation,
} from "./render/camera";
import { pushBeamTiles } from "./render/beam-tiles";
import { pushItemTiles } from "./render/item-tiles";
import { classifyShip, makeViewLod, pushProxyDots, pushShipDot, type LodResult } from "./render/lod";
import { pushPlanetTile } from "./render/planet-tiles";
import { pushPlumeTiles } from "./render/plume-tiles";
import { pushProjectileTiles } from "./render/projectile-tiles";
import { Renderer } from "./render/renderer";
import { pushShipTiles, pushShipTrussTiles } from "./render/ship-tiles";
import { pushSparkTiles } from "./render/spark-tiles";
import { isNatural } from "./render/viz-mode";
import { VizPanel } from "./viz-panel";
import { pushCrewTiles, hitTestCrew } from "./render/crew-tiles";
import { orbitalSpeed } from "./sim/planet";
import { HOME_PLANET, playerOrbitRadius } from "./sim/planets";
import { populateRing } from "./sim/ring";
import { JUNKER_01, JUNKER_02 } from "./sim/blueprints";
import { assignCrewToModule, populateCrew } from "./sim/crew";
import { buildCollisionShapes } from "./sim/physics/collision-shapes";
import { addCargoToShip, effectiveCells, shipBlocksLocalCenter, spawnShipFromBlueprint, totalCrewCapacity } from "./sim/ship";
import { TICK_DT, stepWorld, type SimViewport } from "./sim/tick";
import { createWorld } from "./sim/world";
import { TradePanel } from "./trade-panel";
import type { EntityId, Ship } from "./sim/types";
import { TouchControls } from "./touch-controls";
import { clamp } from "./util/math";

const STICK_DEADZONE = 0.12;

/** Bit 64 in tiles.wgsl: fragment shader dims RGB ~0.28. */
const FLAG_DIM_TILE = 64;

function deadzone(v: number): number {
  return Math.abs(v) < STICK_DEADZONE ? 0 : v;
}

function makeTopbarButton(
  parent: HTMLElement,
  id: string,
  cls: string,
  label: string,
  onClick: () => void,
): HTMLButtonElement {
  const b = document.createElement("button");
  b.id = id;
  b.className = `toolbtn ${cls}`;
  b.textContent = label;
  b.addEventListener("click", onClick);
  parent.appendChild(b);
  return b;
}

function bindHotkey(code: string, fn: () => void): void {
  window.addEventListener("keydown", (e) => {
    if (e.code !== code || e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    fn();
  });
}

function moduleAtWorld(ship: Ship, wx: number, wy: number): number | null {
  const dx = wx - ship.body.pos.x;
  const dy = wy - ship.body.pos.y;
  const c = Math.cos(-ship.body.rot);
  const s = Math.sin(-ship.body.rot);
  const lx = dx * c - dy * s + ship.com.x;
  const ly = dx * s + dy * c + ship.com.y;
  const cs = ship.cellSizeM;
  for (let i = 0; i < ship.modules.length; i++) {
    const m = ship.modules[i];
    if (m.hp <= 0) continue;
    const def = m.def;
    const eff = effectiveCells(def, m.facing);
    const x0 = m.gx * cs, y0 = m.gy * cs;
    const x1 = x0 + eff.w * cs, y1 = y0 + eff.h * cs;
    if (lx >= x0 && lx < x1 && ly >= y0 && ly < y1) return i;
  }
  return null;
}

function shipModuleHitAnywhere(
  world: { ships: Map<number, Ship> },
  wx: number,
  wy: number,
): { ship: Ship; moduleIdx: number } | null {
  for (const ship of world.ships.values()) {
    if (ship.isAsteroid) continue;
    const idx = moduleAtWorld(ship, wx, wy);
    if (idx != null) return { ship, moduleIdx: idx };
  }
  return null;
}

/** Player-controllable: the player ship itself, or one it's boarded. */
function playerOwnsShip(
  world: { ships: Map<number, Ship> },
  player: Ship,
  shipId: number,
): boolean {
  if (shipId === player.id) return true;
  const ship = world.ships.get(shipId);
  return ship?.boardedByShipId === player.id;
}

function seedDebugCargo(ship: ReturnType<typeof spawnShipFromBlueprint>): void {
  const seeds: Array<[string, number]> = [
    ["uranium", 20],
    ["plutonium", 2],
    ["bullets", 50],
    ["shells", 100],
    ["slugs", 100],
    ["missiles", 160],
  ];
  for (const [resource, kg] of seeds) addCargoToShip(ship, resource, kg, 1);
}

/** Places a ship on a circular orbit at `angle` and radius `r`, moving at the
 *  orbital speed for that radius. Defaults to the player's spawn shell, just
 *  beyond the ring. */
function placeInOrbit(
  world: ReturnType<typeof createWorld>,
  ship: Ship,
  angle: number,
  r: number = playerOrbitRadius(HOME_PLANET),
): void {
  const v = orbitalSpeed(world.planet, r);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  ship.body.pos.x = cos * r;
  ship.body.pos.y = sin * r;
  ship.body.vel.x = -sin * v;
  ship.body.vel.y = cos * v;
}

async function boot(): Promise<void> {
  const canvas = document.getElementById("view") as HTMLCanvasElement;

  let renderer: Renderer;
  try {
    renderer = await Renderer.create(canvas);
    await buildCollisionShapes();
  } catch (e) {
    showFatal((e as Error).message);
    return;
  }

  const world = createWorld();
  const player = spawnShipFromBlueprint(world, JUNKER_01, { x: 0, y: 0, rot: 0 });
  player.credits = 10_000;
  seedDebugCargo(player);
  populateCrew(world, player, totalCrewCapacity(player), 10);
  const partner = spawnShipFromBlueprint(world, JUNKER_02, { x: 0, y: -50, rot: 0 });
  partner.credits = 6_000;
  seedDebugCargo(partner);
  populateCrew(world, partner, totalCrewCapacity(partner), 10);
  populateRing(world, HOME_PLANET);

  // Scatter the player and partner onto the same orbital shell as the
  // asteroids, each launched at circular-orbit speed.
  placeInOrbit(world, player, 0);
  // Just along the shell from the player, still clear of the ring.
  placeInOrbit(world, partner, 0.006);

  let selectedCrewId: EntityId | null = null;

  const topBar = document.createElement("div");
  topBar.id = "toolbar-top-right";
  document.body.appendChild(topBar);

  let syncAutoBtn = (): void => {};
  const autoBtn = makeTopbarButton(topBar, "autobrake-btn", "autobrake-btn", "AUTO", () => {
    player.autoBrake = !player.autoBrake;
    syncAutoBtn();
  });
  syncAutoBtn = () => autoBtn.classList.toggle("active", player.autoBrake);

  const imagePanel = new ImageControlsPanel();
  const toggleImage = (): void => {
    imagePanel.toggle();
    imgBtn.classList.toggle("active", imagePanel.isVisible());
  };
  const imgBtn = makeTopbarButton(topBar, "image-btn", "image-btn", "IMAG", toggleImage);
  bindHotkey("KeyO", toggleImage);

  const vizPanel = new VizPanel();
  const vizBtn = makeTopbarButton(topBar, "viz-btn", "viz-btn", "VIEW", () => vizPanel.toggle());
  vizPanel.setOnChange(() => vizBtn.classList.toggle("active", !isNatural()));
  bindHotkey("KeyV", () => vizPanel.toggle());

  let syncModeBtn = (): void => {};
  const modeBtn = makeTopbarButton(topBar, "firemode-btn", "firemode-btn", "", () => {
    player.fireMode = player.fireMode === "mining" ? "combat" : "mining";
    syncModeBtn();
    touch.syncModeButtons(player.fireMode, player.autoBrake);
  });
  syncModeBtn = () => {
    modeBtn.textContent = player.fireMode === "mining" ? "MINE" : "GUNS";
    modeBtn.classList.toggle("mining", player.fireMode === "mining");
  };
  syncModeBtn();

  type CamMode = "normal" | "aligned";
  let camMode: CamMode = "normal";
  const toggleCam = (): void => {
    camMode = camMode === "normal" ? "aligned" : "normal";
    syncCamBtn();
  };
  const camBtn = makeTopbarButton(topBar, "cam-btn", "cam-btn", "", toggleCam);
  const syncCamBtn = (): void => {
    camBtn.textContent = camMode === "aligned" ? "CAMA" : "CAMN";
    camBtn.classList.toggle("active", camMode === "aligned");
  };
  syncCamBtn();
  bindHotkey("KeyC", toggleCam);

  const cam = makeCamera();
  const keys = new InputState(window);
  const touch = new TouchControls(document.body);
  touch.onModeToggle = () => {
    player.fireMode = player.fireMode === "mining" ? "combat" : "mining";
    syncModeBtn();
    touch.syncModeButtons(player.fireMode, player.autoBrake);
  };
  touch.onAutoBrakeToggle = () => {
    player.autoBrake = !player.autoBrake;
    syncAutoBtn();
    touch.syncModeButtons(player.fireMode, player.autoBrake);
  };
  touch.syncModeButtons(player.fireMode, player.autoBrake);

  const gestures = new CanvasGestures(canvas);
  gestures.onPinch(({ factor }) => setZoomTarget(getZoomTarget() * factor));

  const inspector = new Inspector();
  const controls = new ControlsPanel(inspector, () => world);
  const tradePanel = new TradePanel(() => world);
  controls.onOpenTrade = (host, hostCollar) => {
    tradePanel.openForCollar(host, hostCollar);
  };
  const eventLog = new EventLog();
  const build = new BuildMode(player, world, inspector);
  build.setOnToggle((active) => {
    touch.setVisible(!active);
    topBar.style.display = active ? "none" : "";
    if (active) {
      inspector.hide();
      controls.hide();
    }
  });

  gestures.onDragStart(({ clientX, clientY }) => {
    if (!build.isActive()) return;
    const rect = canvas.getBoundingClientRect();
    const w = canvasToWorld(cam, renderer, clientX - rect.left, clientY - rect.top);
    if (build.getTool() === "move") {
      build.beginDrag(w.x, w.y);
      return;
    }
    if (build.canPaintOnDrag()) {
      build.beginPaintStroke(w.x, w.y);
    }
  });
  gestures.onDrag(({ dx, dy, clientX, clientY }) => {
    if (!build.isActive()) return;
    if (build.isDraggingModule()) {
      const worldPerCss = 8 / getZoom();
      const rotDelta = cam.rot - player.body.rot;
      const c = Math.cos(rotDelta);
      const s = Math.sin(rotDelta);
      const localDx = (dx * c - dy * s) * worldPerCss;
      const localDy = (dx * s + dy * c) * worldPerCss;
      build.dragBy(localDx, localDy);
      return;
    }
    if (build.isPainting()) {
      const rect = canvas.getBoundingClientRect();
      const w = canvasToWorld(cam, renderer, clientX - rect.left, clientY - rect.top);
      build.paintAt(w.x, w.y);
      return;
    }
    build.pan(dx, dy, getZoom());
  });
  gestures.onDragEnd(() => {
    if (!build.isActive()) return;
    if (build.isDraggingModule()) build.endDrag();
    if (build.isPainting()) build.endPaintStroke();
  });

  gestures.onTap(({ clientX, clientY }) => {
    const rect = canvas.getBoundingClientRect();
    const w = canvasToWorld(cam, renderer, clientX - rect.left, clientY - rect.top);
    if (build.isActive()) {
      build.handleWorldTap(w.x, w.y);
      return;
    }
    // The camera is at the true (un-extrapolated) player pose, and anything
    // co-moving with it renders at its true coordinate, so picking against sim
    // positions is exact for the player's own ship/crew without any inverse.
    const crewHit = hitTestCrew(world, w.x, w.y, world.time);
    if (crewHit) {
      selectedCrewId = crewHit.id;
      inspector.showCrew(world, crewHit);
      return;
    }
    if (selectedCrewId != null) {
      const crew = world.crew.get(selectedCrewId);
      if (crew && playerOwnsShip(world, player, crew.shipId)) {
        const hit = shipModuleHitAnywhere(world, w.x, w.y);
        if (hit && playerOwnsShip(world, player, hit.ship.id)) {
          assignCrewToModule(world, crew, hit.ship, hit.moduleIdx);
          inspector.showCrew(world, crew);
          return;
        }
      }
      selectedCrewId = null;
    }
    controls.tapAtWorld(world, w.x, w.y);
  });

  let accumulator = 0;
  let lastWall = performance.now() / 1000;
  let fpsAccum = 0;
  let fpsCount = 0;
  let fps = 0;
  const _vp: SimViewport = { cx: 0, cy: 0, halfW: 0, halfH: 0, focusX: 0, focusY: 0 };
  const _shipLod = new Map<Ship, LodResult>();

  let geomLocalX = 0;
  let geomLocalY = 0;
  let geomInit = false;
  const GEOM_SMOOTH_K = 6;

  /** body.rot + π/2 → ship's facing-0 at top (north-up arcade). */
  const ALIGNED_ROT_OFFSET = Math.PI / 2;

  function frame(): void {
    const now = performance.now() / 1000;
    let dt = now - lastWall;
    lastWall = now;
    if (dt > 0.25) dt = 0.25;

    const k = keys.read();
    const t = touch.sample();

    const tThrottle = deadzone(t.leftY);
    const tSteer    = deadzone(t.leftX);
    const tStrafe   = deadzone(t.rightX);

    if (build.isActive()) {
      player.throttle = 0;
      player.steer = 0;
      player.strafe = 0;
      player.fire1 = false;
    } else {
      player.throttle = clamp(k.throttle + tThrottle, -1, 1);
      player.steer  = clamp(k.steer + tSteer, -1, 1);
      player.strafe = clamp(k.strafe + -tStrafe, -1, 1);
      player.fire1 = k.fire1 || t.fire1;
      if (k.toggleFireMode) {
        player.fireMode = player.fireMode === "mining" ? "combat" : "mining";
        syncModeBtn();
        touch.syncModeButtons(player.fireMode, player.autoBrake);
      }
      if (k.toggleAutoBrake) {
        player.autoBrake = !player.autoBrake;
        syncAutoBtn();
        touch.syncModeButtons(player.fireMode, player.autoBrake);
      }
    }

    accumulator += dt;
    if (cam.worldW > 0 && cam.worldH > 0) {
      _vp.cx = cam.x; _vp.cy = cam.y;
      _vp.halfW = cam.worldW * 0.5; _vp.halfH = cam.worldH * 0.5;
      // Active zone is anchored on the player ship, not the (possibly panned)
      // camera — "2 km from our ship", per the LOD design.
      _vp.focusX = player.body.pos.x; _vp.focusY = player.body.pos.y;
    }
    const vp = (cam.worldW > 0 && cam.worldH > 0) ? _vp : undefined;
    while (accumulator >= TICK_DT) {
      stepWorld(world, vp);
      accumulator -= TICK_DT;
    }

    if (world.events.length > 0) {
      for (const ev of world.events) {
        if (ev.kind !== "module-destroyed") continue;
        let msg: string;
        let level: EventLevel;
        if (ev.shipId === player.id) {
          msg = `LOST ${ev.defName.toUpperCase()} (${ev.cause})`;
          level = "destroy";
        } else if (ev.shipIsAsteroid) {
          msg = `mined ${ev.defName}`;
          level = "gain";
        } else {
          msg = `${ev.shipName}: ${ev.defName} destroyed (${ev.cause})`;
          level = "hit";
        }
        eventLog.push(msg, level);
      }
      world.events.length = 0;
    }

    cam.time = world.time;
    const inBuild = build.isActive();

    const target = shipBlocksLocalCenter(player) ?? { x: player.com.x, y: player.com.y };
    if (!geomInit) {
      geomLocalX = target.x;
      geomLocalY = target.y;
      geomInit = true;
    } else {
      const k = 1 - Math.exp(-GEOM_SMOOTH_K * dt);
      geomLocalX += (target.x - geomLocalX) * k;
      geomLocalY += (target.y - geomLocalY) * k;
    }
    const offLocalX = geomLocalX - player.com.x;
    const offLocalY = geomLocalY - player.com.y;
    const sr = Math.sin(player.body.rot);
    const cr = Math.cos(player.body.rot);
    const centerWX = player.body.pos.x + offLocalX * cr - offLocalY * sr;
    const centerWY = player.body.pos.y + offLocalX * sr + offLocalY * cr;

    const aligned = inBuild || camMode === "aligned";
    cam.rot = aligned ? player.body.rot + ALIGNED_ROT_OFFSET : 0;
    if (inBuild) {
      const cosR = Math.cos(cam.rot);
      const sinR = Math.sin(cam.rot);
      cam.x = centerWX + build.panX * cosR - build.panY * sinR;
      cam.y = centerWY + build.panX * sinR + build.panY * cosR;
    } else {
      cam.x = centerWX;
      cam.y = centerWY;
    }
    // Camera velocity drives motion blur (shader: dVel = inst.vel - cam.vel).
    // Build mode is an aligned view locked to the player, so the camera carries
    // the player's velocity/spin too — otherwise the still-moving ship you're
    // editing would smear at full world speed instead of sitting blur-free.
    cam.vx = player.body.vel.x;
    cam.vy = player.body.vel.y;
    cam.angVel = aligned ? player.body.angVel : 0;
    updateZoomAnimation(dt);
    renderer.resizeToCanvas();
    syncCameraToCanvas(cam, renderer);
    renderer.beginFrame(cam);
    // Planet first so it sits behind every entity (they orbit clear of it).
    pushPlanetTile(renderer, world.planet);
    if (inBuild) {
      renderer.setTileFlagMask(FLAG_DIM_TILE);
      pushItemTiles(renderer, world.items.values());
      for (const ship of world.ships.values()) {
        if (ship === player) continue;
        pushShipTrussTiles(renderer, ship);
        pushShipTiles(renderer, ship, world.time);
      }
      renderer.setTileFlagMask(0);
      pushShipTrussTiles(renderer, player, build.getActiveModule());
      pushShipTiles(renderer, player, world.time, build.getActiveModule());
    } else {
      const natural = isNatural();
      const view = makeViewLod(cam, renderer.pixelW, getZoom());
      // Classify every ship once; reuse across the truss/detail passes so the
      // two stay consistent. Off-screen ships ("cull") become pure data — never
      // pushed to the GPU; tiny/distant ones collapse to a single dot.
      const shipLod = _shipLod;
      shipLod.clear();
      for (const ship of world.ships.values()) {
        shipLod.set(ship, classifyShip(view, ship));
      }
      for (const ship of world.ships.values()) {
        if (shipLod.get(ship)!.full) pushShipTrussTiles(renderer, ship);
      }
      if (natural) {
        pushSparkTiles(renderer, world.sparks);
        pushProjectileTiles(renderer, world.projectiles.values());
      }
      pushItemTiles(renderer, world.items.values(), view);
      if (natural) pushPlumeTiles(renderer, world);
      // Dormant far asteroids — always dots (no module data to draw in full).
      pushProxyDots(renderer, view, world.asteroids);
      for (const ship of world.ships.values()) {
        const lod = shipLod.get(ship)!;
        if (lod.cull) continue;
        if (lod.full) pushShipTiles(renderer, ship, world.time);
        // Cross-fade: dot rides on top of the still-drawn silhouette.
        if (lod.dotAlpha > 0) pushShipDot(renderer, ship, view, lod.dotAlpha);
      }
      if (natural) {
        pushBeamTiles(renderer, world.beams);
      }
      pushCrewTiles(renderer, world, world.time, selectedCrewId);
    }
    renderer.endFrame();

    controls.refresh();
    inspector.refresh(world);
    tradePanel.refresh();

    fpsAccum += dt;
    fpsCount++;
    if (fpsAccum > 0.5) {
      fps = fpsCount / fpsAccum;
      fpsAccum = 0;
      fpsCount = 0;
    }
    updateHud(world, player, fps);

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);
}

boot().catch((e) => showFatal((e as Error).message ?? String(e)));
